import React, { useId, useRef, useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import "./ScientificEvidence.css";

// Keep each study's metric and protocol separate. Provenance: research/*benchmark*
// and research/genebench-pro-evidence-2026-10.md. These are not Cornucopia results.
const studies = [
  {
    id: "genomics",
    label: "GeneBench-Pro",
    title: "Scientific judgment in genomics",
    metric: "Pass rate · 10 public genomics problems",
    rows: [
      { label: "Sonnet 5.5 · Max", value: 39.0, accent: true },
      { label: "GPT-6 Astra · Max", value: 36.0 },
      { label: "GPT-6.1 Sol · Max", value: 33.0 },
      { label: "Opus 5.5 · Max", value: 30.0 },
      { label: "Muse Spark 1.3 · Max", value: 26.0 },
    ],
    context: "Mercor APEX · current published results",
    date: "Leaderboard checked 26 October 2026",
    takeaway: "Even the highest-scoring model here passes fewer than half of its runs. The task is to turn messy genomics data into a correct estimate, choosing and checking the analysis along the way.",
    links: [
      { label: "Current leaderboard", href: "https://www.mercor.com/apex/oss-benchmarks/genebench-leaderboard/" },
      { label: "Research paper · v2", href: "https://www.biorxiv.org/content/10.64898/2026.06.29.735386v2" },
    ],
    methodLinks: [
      { label: "Original full-text PDF", href: "https://cdn.openai.com/pdf/21938268-21af-442f-af93-3b2249afb241/genebench-pro.pdf" },
      { label: "Benchmark design", href: "https://openai.com/index/introducing-genebench-pro/" },
      { label: "Separate v13 results", href: "https://openai.com/index/gpt-6-astra/" },
    ],
    methods: [
      "This is Mercor APEX’s evaluation of the ten publicly released GeneBench-Pro problems, checked on 26 October 2026. The chart reproduces its five featured model configurations. Scores average ten runs per problem; a run passes only when every reported value matches the hidden answer within tolerance. The leaderboard does not supply uncertainty intervals for these displayed results.",
      "The problems use simulated data with known answers, covering genetics, functional genomics and related analyses. This small public subset is useful for comparing the listed settings, but it is not the full 129-problem suite or a measure of all genomics research. Its public availability also creates a potential contamination concern.",
      "The original June preprint reported 28.7% for GPT-5.6 Sol and 31.5% for Sol Pro on all 129 problems. A separate OpenAI release reports v13 scores of 37.1% for GPT-6 Astra and 32.3% for Sol. Different subsets, versions and evaluation settings must not be combined into one ranking; the chart above uses only the current Mercor evaluation.",
      "External experts reviewed 82 problems in the original study; that is not a measured human-solving baseline. No human pass-rate bar is inferred. These computational results do not measure wet-lab performance, clinical success or Cornucopia’s agents.",
    ],
  },
  {
    id: "drug-discovery",
    label: "DrugDiscoveryBench",
    title: "Early drug-discovery workflows",
    metric: "Task pass rate · 82 tasks",
    rows: [{ label: "Leading published agent setting", value: 68.7, accent: true }],
    context: "GPT-6 Astra · mini-SWE-agent · max effort · mean of 3 runs",
    date: "Leaderboard checked 26 October 2026",
    takeaway: "The agents already had scientific tools and a data lake. Missed constraints and errors in scientific reasoning still broke workflows.",
    links: [
      { label: "Leaderboard & methods", href: "https://labs.scale.com/leaderboard/drugdiscoverybench" },
      { label: "Research paper", href: "https://static.scale.com/uploads/6691558a94899f2f65a87a75/DrugDiscoveryBench.pdf" },
    ],
    methods: [
      "The benchmark covers computational and retrieval tasks before candidate selection. Agents receive 226 biomedical functions, a data lake and scientific software. Each setting averages three runs with a 120-minute timeout. A pass requires full credit from an LLM judge using an expert-written rubric; errors and timeouts count as failures.",
      "68.7% is the leading published setting at the snapshot date, not a ceiling for all models. The leaderboard reports ±2.5 without clearly defining that notation, so no confidence interval is drawn here. No separately tested human cohort is reported.",
      "Expert procedures improved task recovery in the paper. That supports testing domain workflows and intermediate checks, as well as connecting tools. These are computational tasks, not complete drug-development programs.",
    ],
  },
  {
    id: "science-agent",
    label: "ScienceAgentBench",
    title: "Code execution and task success",
    metric: "102 research tasks · original 2025 study",
    rows: [
      { label: "Ran and saved an output", value: 92.2 },
      { label: "Met the task’s success criteria", value: 32.4, accent: true },
    ],
    context: "Claude 3.5 Sonnet · Self-Debug · best of 3 runs",
    date: "Historical evaluation · a revised split was released in 2026",
    takeaway: "A successful tool call is not enough. We need to check that the output answers the scientific question.",
    links: [
      { label: "Study, Table 3", href: "https://arxiv.org/pdf/2410.05080v3#page=8" },
      { label: "Benchmark updates", href: "https://github.com/OSU-NLP-Group/ScienceAgentBench" },
    ],
    methods: [
      "The original benchmark contains 102 tasks across four disciplines. This configuration uses Claude 3.5 Sonnet (2024-06-20), Self-Debug and no extra expert knowledge. Both bars describe the same selected runs: best of three per task, prioritizing success, then execution and other criteria.",
      "Execution requires error-free code and a saved output. Success adds task-specific prediction, metric or figure criteria, with some figures judged by GPT-4o. Human evaluation in this paper means grading agent outputs, not humans taking the benchmark.",
      "32.4% is this configuration’s result, not the best score in the paper. The authors released a verified split in April 2026 to reduce false negatives. This chart preserves the original study; it does not measure current frontier performance.",
    ],
  },
];

const contextStudy = {
    id: "human-reference",
    label: "A human comparison",
    title: "Reading scientific figures",
    metric: "Scientific figure questions · LAB-Bench FigQA",
    rows: [
      { label: "Sonnet 4.6 · without tools", value: 58.8 },
      { label: "Sonnet 4.6 · image-cropping tool", value: 77.1, accent: true },
      { label: "Expert humans · 2024 reference", value: 77.0, human: true },
    ],
    context: "Model results: February 2026 · max effort · mean of 5 runs",
    date: "Human reference comes from a separate, earlier study",
    takeaway: "Giving the same model an image-cropping tool improved its reading of scientific figures. DrugDiscoveryBench also found that expert procedures helped agents recover failed tasks.",
    links: [
      { label: "Model evaluation, p. 34", href: "https://www-cdn.anthropic.com/bbd8ef16d70b7a1665f14f306ee88b53f686aa75.pdf#page=34" },
      { label: "Human study, Table 2", href: "https://arxiv.org/html/2407.10362v3" },
    ],
    methodLinks: [{ label: "Expert procedures in DrugDiscoveryBench", href: "https://static.scale.com/uploads/6691558a94899f2f65a87a75/DrugDiscoveryBench.pdf" }],
    methods: [
      "Anthropic evaluated Sonnet 4.6 with adaptive thinking at maximum effort, averaging five runs in each condition. The system card reports 58.8% without tools and 77.1% with image cropping. These bars show reported means; uncertainty intervals are available in the source. The 0.1-point difference from the human reference is not evidence of superiority.",
      "The 77.0% human accuracy is from the original 2024 LAB-Bench study. PhD-level biologists answered questions they had not authored, with research tools and internet access. They had five to eight days per quiz. Human assessments sampled the benchmark; unsure answers counted as incorrect.",
      "This is a historical reference, not a contemporaneous human-versus-model trial with identical resources. It concerns figure interpretation, not all scientific work or drug discovery. It shows why tools matter; it does not establish the performance of Cornucopia’s agents.",
      "Separately, DrugDiscoveryBench supplied expert-written procedures for previously failed tasks. This guidance improved pooled task recovery across agents. It was a workflow intervention, not the image-tool intervention plotted above or a new independent success rate for this model.",
    ],
  };

function StudyLinks({ links }) {
  return <div className="evidence-sources">{links.map(({ href, label }) => <a key={href} href={href} target="_blank" rel="noreferrer">{label}<ArrowUpRight size={14} aria-hidden="true" /></a>)}</div>;
}

function StudyContent({ study, showTitle = true }) {
  const id = useId();
  return (
    <div className="evidence-study">
      <figure className={`evidence-chart${study.rows.length === 1 ? " evidence-chart-single" : ""}`} aria-labelledby={`${id}-chart-caption`}>
        <figcaption id={`${id}-chart-caption`}>{study.metric}</figcaption>
        <div className="evidence-chart-scale" aria-hidden="true"><span>0%</span><span>50%</span><span>100%</span></div>
        <div className="evidence-chart-bars">
          {study.rows.map(({ label, value, accent, human }) => (
            <div className={`evidence-bar-row${accent ? " evidence-bar-accent" : ""}${human ? " evidence-bar-human" : ""}`} key={label}>
              <div className="evidence-bar-label"><span>{label}</span><strong>{value.toFixed(1)}%</strong></div>
              <div className="evidence-bar-track" aria-hidden="true"><span style={{ width: `${value}%` }} /></div>
            </div>
          ))}
        </div>
        <p className="evidence-chart-context">{study.context}</p>
        <p className="evidence-chart-date">{study.date}</p>
      </figure>
      <div className="evidence-reading">
        {showTitle && <h3>{study.title}</h3>}
        <p className="evidence-takeaway">{study.takeaway}</p>
        <StudyLinks links={study.links} />
        <details className="evidence-detail">
          <summary>Methods & limitations <Plus size={17} aria-hidden="true" /></summary>
          <div>
            {study.methods.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {study.methodLinks && <StudyLinks links={study.methodLinks} />}
          </div>
        </details>
      </div>
    </div>
  );
}

export function ScientificContext() {
  const id = useId();
  return (
    <section className="scientific-context journey-shell" id="scientific-context" aria-labelledby={`${id}-title`}>
      <header className="evidence-heading">
        <h2 id={`${id}-title`}>The working context <em>changes the result.</em></h2>
      </header>
      <StudyContent study={contextStudy} showTitle={false} />
    </section>
  );
}

export default function ScientificEvidence() {
  const [selected, setSelected] = useState(0);
  const tabs = useRef([]);
  const id = useId();
  const study = studies[selected];

  const handleKeys = (event) => {
    let next;
    if (event.key === "ArrowRight") next = (selected + 1) % studies.length;
    if (event.key === "ArrowLeft") next = (selected - 1 + studies.length) % studies.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = studies.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  };

  return (
    <section className="scientific-evidence journey-shell" id="scientific-evidence" aria-labelledby={`${id}-title`}>
      <header className="evidence-heading">
        <h2 id={`${id}-title`}>Science is a demanding <em>test for AI.</em></h2>
        <p>We believe general-purpose AI alone is not enough for drug discovery. We test each agent on the scientific work it is meant to do.</p>
      </header>
      <div className="evidence-tabs" role="tablist" aria-label="Scientific benchmarks" onKeyDown={handleKeys}>
        {studies.map(({ id: studyId, label }, index) => (
          <button key={studyId} type="button" role="tab" id={`${id}-tab-${studyId}`} aria-controls={`${id}-panel`} aria-selected={selected === index} tabIndex={selected === index ? 0 : -1} ref={(element) => { tabs.current[index] = element; }} onClick={() => setSelected(index)}>{label}</button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${study.id}`} tabIndex={0} className="evidence-panel">
        <StudyContent study={study} key={study.id} />
      </div>
      <p className="evidence-scope">Each benchmark measures different work. These are published evaluations, not results from Cornucopia.</p>
    </section>
  );
}
